import {
  createProductResponseSchema,
  localizedProductRequiredResponseSchema,
  localizedProductResponseSchema,
  merchantProductSchema,
  modifierDeleteResponseSchema,
  modifierGroupResponseSchema,
  productsModifiersDisabledResponseSchema,
  recommendedProductSchema,
  recommendationsResponseSchema,
  publishedProductCardResponseSchema,
  restaurantProductSchema,
  restaurantProductsResponseSchema,
  type CreateProductResult,
  type ListResult,
  type LocalizedProduct,
  type LocalizedText,
  type MeasurementType,
  type MerchantProduct,
  type ModifierDeleteResult,
  type ModifierGroupWriteResult,
  type PaginationParams,
  type ProductStatus,
  type ProductsModifiersDisabledResult,
  type RecommendationsResult,
  type ProductWriteResult,
  type PublishedProductCardResponse,
  type RestaurantProduct,
  type RestaurantProductsResult,
} from '../model';
import { http } from '@noor/api';
import type { CreateModifierGroupInput } from './modifier-api';
import {
  deleteOne,
  deleteOneParsed,
  deleteWithBody,
  getFlatList,
  getList,
  getOne,
  patchList,
  patchOne,
  postOne,
  postOneWithList,
  postVoid,
  putOne,
  putVoid,
  type RequestOptions,
} from './http';

// Product Service. A product is owned by the merchant and surfaced in branches
// through per-restaurant overlays (`RestaurantProduct`) that carry the
// branch-local price, stock and availability.

/**
 * Filters for `GET /merchant/products/published` — published dishes visible to customers.
 */
export type ListPublishedProductsParams = PaginationParams & {
  restaurant_id?: string;
  category_id?: string;
  name?: string;
};

/**
 * Filters for `GET /merchant/products` — the merchant's whole catalog,
 * deduplicated across branches. Scoped by the bearer token, so it takes no
 * merchant or restaurant id; `name` is a free-text search.
 */
export type ListMerchantProductsParams = PaginationParams & {
  name?: string;
};

export interface UpdateRecommendationsInput {
  recommended_product_ids: string[];
}

/**
 * Filters for `GET /merchant/products/by-restaurant` — one branch's menu,
 * with that branch's real price/stock/status overlaid. Stop-listed products are
 * included, so the merchant can re-enable them.
 */
export type ListMerchantProductsByRestaurantParams = PaginationParams & {
  category_id?: string;
  /**
   * Case-insensitive substring match on the product name — the same filter
   * `GET /merchant/products` documents. NOT in the spec for this route yet;
   * it is sent because the merchant catalog's search box must narrow the
   * BRANCH's menu, and an unknown query param is ignored rather than rejected
   * (the gateway's strict-field checking is on request BODIES). Until the
   * backend honours it the client also narrows what comes back, so the box
   * works either way.
   */
  name?: string;
  /**
   * Moderation lifecycle filter, matching the `lifecycle_status` the product
   * rows carry. Also NOT yet in the spec for this route — sent ahead of the
   * backend supporting it, on the same terms as `name` above.
   */
  status?: string;
};

/**
 * Selling hours as they go on a write (`availability_window`). Both members are
 * "HH:MM"; an empty or absent side leaves that side unlimited.
 *
 * On the body that carries it: the KEY absent = leave the stored hours alone,
 * `null` = remove them (the dish / branch is sold all day again), an object =
 * set them.
 */
export interface AvailabilityWindow {
  available_from?: string;
  available_to?: string;
}

/**
 * The branch-local overlay supplied when creating a product or stocking it in
 * additional branches (`v1RestaurantProductInput`).
 *
 * NO `status` member: the input shape carries only id/price/stock, and the
 * gateway rejects unknown fields (verified live: `400 unknown field
 * "restaurants[1].status"`). Branch-local status changes go through the PATCH
 * overlay endpoint, whose `RestaurantProductPatch` does take one.
 */
export interface RestaurantProductInput {
  restaurant_id: string;
  price?: number;
  stock_quantity?: number;
  /** This branch's own selling hours; absent = inherit the dish's. */
  availability_window?: AvailabilityWindow | null;
}

/**
 * `POST /merchant/products` body — creates one product across one or more branches.
 *
 * `name`/`description` are `LocalizedText` (all three languages at once), not
 * plain strings: the merchant editor owns the translations directly rather than
 * writing whichever locale the request happened to carry.
 */
export interface CreateProductInput {
  product_category_id: string;
  name: LocalizedText;
  description?: LocalizedText;
  composition?: LocalizedText | null;
  nutrition?: {
    calories?: number | null;
    fats?: number | null;
    proteins?: number | null;
  } | null;
  measurement_type?: MeasurementType;
  cook_time_seconds?: number;
  is_always_ready?: boolean;
  images?: string[];
  /**
   * The product's own price in so'm — what every branch charges unless it
   * names its own. REQUIRED by the API now that the create names no branches
   * (there is no branch price left to derive it from); `optional` on the wire
   * only so an absent price can be told apart from an explicit 0.
   */
  price?: number;
  /**
   * Groups to put on the new dish, replacing the inline `modifiers` field
   * (DELETED in v1.00.227). Each entry links an existing catalogue group by id
   * OR describes a new one created in the same transaction — and `new_group`
   * takes `LocalizedText` names, so a create no longer needs the follow-up
   * translation pass the plain-string inline field used to force.
   */
  modifier_groups?: ProductModifierGroupInput[];
  /** 1..200 branches, deduped by `restaurant_id`; each carries its own price. */
  restaurants?: RestaurantProductInput[];
  /** 18+ product; omitted means not age-restricted. */
  is_adult?: boolean;
  /** IKPU/SPIC classifier code, required by OFD fiscalization. */
  ikpu_code?: string;
  /** The product's category is subject to mandatory goods marking. */
  has_marked_code?: boolean;
  /** Marking (Data Matrix) code; required when `has_marked_code` is true. */
  marked_code?: string;
  /** VAT %. Omitted = the brand's rate; `0` = no VAT. */
  vat_percent?: number;
  /** OFD measurement-unit classifier code (Soliq tasnif). */
  unit_code?: string;
  /** Net amount of one unit in the `unit_code` unit; 0 clears it. */
  measure_amount?: number;
  /** The dish's selling hours; absent = sold all day. */
  availability_window?: AvailabilityWindow;
}

/**
 * `PATCH /merchant/products/{id}` body — a partial update of the shared product.
 * Omitting a field leaves it untouched; `modifiers` is wrapped so that "don't
 * touch" (absent) differs from "replace/clear" (present-but-empty).
 *
 * NO `images` member: API v1.00.141 dropped it (`v1ImageList` no longer
 * exists) and moved gallery writes out of this body — as of the 2026-08-14
 * revision they go to the Image Service ({@link imageApi.upsert} /
 * {@link imageApi.remove}, entity type `product`). The gateway
 * REJECTS unknown fields (400 `unknown field`), so sending the old wrapped
 * shape does not degrade quietly, it fails the whole update.
 */
export interface UpdateProductInput {
  /** All languages at once; omit to leave every translation untouched. */
  name?: LocalizedText;
  description?: LocalizedText;
  composition?: LocalizedText | null;
  nutrition?: {
    calories?: number | null;
    fats?: number | null;
    proteins?: number | null;
  } | null;
  price?: number;
  measurement_type?: MeasurementType;
  cook_time_seconds?: number;
  /**
   * UNDOCUMENTED on the update body: `ProductServiceUpdateProductBody` omits it
   * even though `v1CreateProductsRequest` accepts it and both read shapes
   * return it. Sent anyway — without it the toggle is uneditable after create,
   * and a field the wire round-trips everywhere else is far more likely a
   * codegen gap than a deliberate create-only flag.
   */
  is_always_ready?: boolean;
  /** 18+ flag; sent on every update so the switch is editable. */
  is_adult?: boolean;
  // NO `modifiers` member: v1.00.227 removed it from the PATCH outright, with
  // no replacement in this body. A dish's groups change through the dedicated
  // routes — attach/detach below, and the catalogue endpoints (`modifierApi`)
  // for the groups' own fields. The gateway rejects unknown fields, so sending
  // the old wrapped shape fails the whole update.
  category_id?: string;
  /**
   * OFD fiscalization codes, added to the update body in API v1.00.155 (they
   * were create-only before). Each is `nil = don't touch`, so omitting one
   * leaves the stored value alone.
   */
  ikpu_code?: string;
  has_marked_code?: boolean;
  marked_code?: string;
  /** VAT %; omitted leaves the stored rate untouched, `0` = no VAT. */
  vat_percent?: number;
  unit_code?: string;
  /** Net amount of one unit in the `unit_code` unit; 0 clears it. */
  measure_amount?: number;
  /** Selling hours: absent = don't touch, `null` = remove them, object = set. */
  availability_window?: AvailabilityWindow | null;
}

/**
 * `v1ProductModifierGroupInput` — one catalogue group put on a dish being
 * created: an EXISTING group by id, or a NEW one described inline and created
 * in the same transaction. `group_id` and `new_group` are mutually exclusive —
 * neither or both is invalid input. The dish never owns the group either way;
 * it links to it.
 */
export interface ProductModifierGroupInput {
  sort_order?: number;
  group_id?: string;
  new_group?: CreateModifierGroupInput;
}

/**
 * `ProductServiceAttachModifierGroupBody` — `POST /merchant/products/{id}/modifier-groups`
 * now ATTACHES a catalogue group to the dish (it used to create a
 * product-owned group). Same exclusive pair as the create-time input, plus the
 * moderation-queue branch filter.
 */
export interface AttachModifierGroupInput extends ProductModifierGroupInput {
  restaurant_id?: string;
}

/**
 * One modifier option's per-restaurant state inside a branch patch
 * (`v1ModifierOptionStatePatch`, v1.00.227). Naming an option is what makes it
 * affected at all; every field is `absent = don't touch`.
 *
 * `stop_counting` returns the option to UNTRACKED (nobody counts ketchup) —
 * NOT the same as `stock_quantity: 0`, which means counted and none left; the
 * pair together is a contradiction the backend rejects. `clear_price` drops
 * the branch's price override back to the catalogue price; a separate flag
 * rather than an empty wrapper because Kong's JSON→protobuf bridge drops empty
 * objects on the way in.
 */
export interface ModifierOptionStatePatch {
  option_id: string;
  /** 0..7200; `0` = counted and none left. */
  stock_quantity?: number;
  /** `STOP_LIST` turns the option off at this branch regardless of its count. */
  status?: ProductStatus;
  stop_counting?: boolean;
  /** This branch's price for the option, in UZS so'm. */
  price?: number;
  clear_price?: boolean;
}

/**
 * One branch's patch in `PATCH /merchant/products/{id}/restaurant-products`.
 * `stock_quantity` takes a bare number or null — the backend rejects the old
 * `{ value }` wrapper ("takes a bare value or null, not an object"). Absent =
 * leave unchanged, `0` = counted and none left. The plain `stop_counting` flag
 * (v1.00.227) clears the count — sending both it and a value is rejected.
 */
export interface RestaurantProductPatch {
  restaurant_id: string;
  price?: number;
  stock_quantity?: number | null;
  status?: ProductStatus;
  /** Per-option state at this branch (v1.00.227). Empty = touch none of them. */
  modifier_option_states?: ModifierOptionStatePatch[];
  stop_counting?: boolean;
  /** This branch's own hours: absent = don't touch, `null` = back to the dish's. */
  availability_window?: AvailabilityWindow | null;
}

// Catalog reads use the merchant actor routes, which include unpublished
// records and the fiscal fields required by the editor. Client routes resolve
// localized text for display and are unsuitable for seeding merchant forms.

export const productApi = {
  /** Read the products configured as "buy together" for one product. */
  listRecommendations(
    productId: string,
    options?: RequestOptions,
  ): Promise<RecommendationsResult> {
    // Recommendations uses the gateway's list envelope:
    // `{ list: [...], data: {}, meta }`.  Do not use `getOne` here because it
    // unwraps `data` and would discard the actual recommendation rows.
    return getList(
      `merchant/products/${productId}/recommendations`,
      recommendedProductSchema,
      undefined,
      options,
    );
  },

  /** Replace the complete recommendation list (an empty list clears it). */
  updateRecommendations(
    productId: string,
    input: UpdateRecommendationsInput,
  ): Promise<RecommendationsResult> {
    return putOne(
      `merchant/products/${productId}/recommendations`,
      input,
      recommendationsResponseSchema,
    );
  },

  /**
   * Published products visible to clients — `GET /merchant/products/published`.
   * Without `restaurant_id`: merchant's published catalog (catalog price, effective_price == price, no stock).
   * With `restaurant_id`: branch-specific prices, discounts, stock and stop-list status.
   */
  listPublished(
    params?: ListPublishedProductsParams,
    options?: RequestOptions,
  ): Promise<ListResult<MerchantProduct>> {
    return getList('merchant/products/published', merchantProductSchema, params, options);
  },

  /**
   * Published product card — `GET /merchant/products/{id}/published`.
   * Answers with `{ client_visible: true, product }` or `{ client_visible: false, product: null }`.
   */
  getPublished(
    id: string,
    params?: { restaurant_id?: string },
    options?: RequestOptions,
  ): Promise<PublishedProductCardResponse> {
    return getOne(
      `merchant/products/${id}/published`,
      publishedProductCardResponseSchema,
      params,
      options,
    );
  },

  /**
   * The merchant's whole catalog, deduplicated across branches — `GET
   * /products/by-merchant`. Scoped by the token, so no branch is addressed and
   * `price` is the lowest across the branches stocking each product.
   */
  list(
    params?: ListMerchantProductsParams,
    options?: RequestOptions,
  ): Promise<ListResult<MerchantProduct>> {
    return getList('merchant/products', merchantProductSchema, params, options);
  },

  /**
   * One branch's menu — `GET /merchant/products/by-restaurant`, with that
   * branch's own price/stock/status overlaid and `effective_price` resolved
   * against its active campaigns.
   */
  listByRestaurant(
    restaurantId: string,
    params?: ListMerchantProductsByRestaurantParams,
    options?: RequestOptions,
  ): Promise<ListResult<MerchantProduct>> {
    return getList(
      'merchant/products/by-restaurant',
      merchantProductSchema,
      { ...params, restaurant_id: restaurantId },
      options,
    );
  },

  /**
   * The merchant's best-selling products (deduplicated across branches). Takes
   * a FLAT `limit`, not the nested `pagination.limit` the other lists use.
   */
  listTop(
    params: { limit?: number } = {},
    options?: RequestOptions,
  ): Promise<ListResult<MerchantProduct>> {
    return getFlatList('merchant/products/top', merchantProductSchema, params, options);
  },

  /**
   * Get a single product by id — `GET /merchant/products/{id}`.
   *
   * Answers with `v1LocalizedProduct`: EVERY translation in one call. That is
   * new in v1.00.155 and it retires the old workaround of reading the same
   * product once per language with `x-locale` and reassembling (CLAUDE.md #12)
   * — there is now a localized merchant read, so the editor is seeded by a
   * single request.
   */
  async get(id: string, options?: RequestOptions): Promise<LocalizedProduct> {
    const { product } = await getOne(
      `merchant/products/${id}`,
      localizedProductRequiredResponseSchema,
      undefined,
      options,
    );
    return product;
  },

  /**
   * Get a single product as one branch sells it — `GET
   * /products/{id}/by-merchant/by-restaurant`. This is the read that seeds the
   * editor: `price` is the branch's overlay, not the shared base price, so
   * saving it back cannot overwrite a branch-local price with the global one.
   */
  async getByRestaurant(
    id: string,
    restaurantId: string,
    options?: RequestOptions,
  ): Promise<LocalizedProduct> {
    const { product } = await getOne(
      `merchant/products/${id}/by-restaurant`,
      localizedProductRequiredResponseSchema,
      { restaurant_id: restaurantId },
      options,
    );
    return product;
  },

  /**
   * Create a product. Returns both halves of the split envelope: the created
   * product (with every translation and the server-minted id) and the
   * per-branch overlays it was stocked in.
   */
  async create(
    input: CreateProductInput,
  ): Promise<{ product: LocalizedProduct; restaurantProducts: RestaurantProduct[] }> {
    const { data, list }: CreateProductResult = await postOneWithList(
      'merchant/products',
      input,
      createProductResponseSchema,
    );
    if (!data.product) {
      // A create cannot be gated — there is no prior product to moderate — so
      // an envelope with no product is contract drift, not the pending branch.
      throw new Error('Product create returned no product');
    }
    return { product: data.product, restaurantProducts: list };
  },

  /**
   * Update the shared product record — `PATCH /merchant/products/{id}`.
   *
   * Answers with EITHER the updated product (all translations) or, when the
   * merchant's writes are gated behind moderation, only a `pending_change`
   * receipt. Both are successes, so the result is returned as-is rather than
   * unwrapped to a product the caller might not have received.
   */
  update(id: string, input: UpdateProductInput): Promise<ProductWriteResult> {
    return patchOne(`merchant/products/${id}`, input, localizedProductResponseSchema);
  },

  /**
   * Switch dishes' modifiers off (or back on) wholesale — `PUT
   * /merchant/products/modifiers-disabled`. One dish is a one-element list;
   * the route has no per-product form. `disabled` is the DESIRED state, not a
   * toggle, so repeating a value changes nothing. Not moderation-gated: the
   * answer names what flipped, never a `pending_change`.
   *
   * The server takes 1..100 ids per call.
   */
  setModifiersDisabled(
    productIds: string[],
    disabled: boolean,
  ): Promise<ProductsModifiersDisabledResult> {
    return putOne(
      'merchant/products/modifiers-disabled',
      { product_ids: productIds, disabled },
      productsModifiersDisabledResponseSchema,
    );
  },

  /** Delete a product from the merchant's catalog. */
  remove(id: string): Promise<void> {
    return deleteOne(`merchant/products/${id}`);
  },

  /** Drop merchant overrides so the named fields follow the menu source again. */
  resetOverrides(id: string, fields: string[]): Promise<void> {
    return postVoid(`products/${id}/reset-overrides`, { fields });
  },

  // Modifier groups became CATALOGUE entities in API v1.00.227 (`modifierApi`).
  // What remains product-scoped is only the LINK between a dish and a group:
  // the two routes below. The old product-owned group/option routes
  // (`PUT .../modifier-groups/{gid}`, `POST|PUT|DELETE .../options[/{oid}]`)
  // are deleted from the spec.

  /**
   * Put one catalogue group on a dish — `POST /merchant/products/{id}/modifier-groups`.
   * Either an existing group by `group_id`, or a `new_group` described inline
   * and created + attached in one transaction (exactly one of the two).
   * Moderation-gated: answers the localized group OR a `pending_change`
   * receipt.
   */
  attachModifierGroup(
    productId: string,
    body: AttachModifierGroupInput,
  ): Promise<ModifierGroupWriteResult> {
    return postOne(
      `merchant/products/${productId}/modifier-groups`,
      body,
      modifierGroupResponseSchema,
    );
  },

  /**
   * Take one group off THIS dish — `DELETE
   * /products/{id}/modifier-groups/{groupId}`. A DETACH, not a delete: the
   * catalogue group and every other dish carrying it survive (deleting the
   * group itself is `modifierApi.remove`). Parsed for the moderation receipt a
   * gated detach answers with.
   */
  detachModifierGroup(productId: string, groupId: string): Promise<ModifierDeleteResult> {
    return deleteOneParsed(
      `merchant/products/${productId}/modifier-groups/${groupId}`,
      modifierDeleteResponseSchema,
    );
  },

  // A product's gallery used to be written through four routes here —
  // `POST /products/{id}/images`, `PUT`/`DELETE /products/images/{imageId}`
  // and `.../set-main`. All four are GONE as of the 2026-08-14 spec revision:
  // photos now write through the Image Service (`imageApi`), where an add, an
  // in-place replace and a promote-to-cover are all items in ONE batched
  // upsert. `reorderImages` below is the only image route still owned by the
  // Product Service.

  /**
   * Set the whole gallery order in one call — `PUT
   * /products/{product_id}/images/reorder`. `ordered_ids` must be EXACTLY the
   * product's current image ids (a permutation — no additions, no omissions).
   * The first id becomes the cover, so this doubles as a set-main and the two
   * must not be issued as a pair (the reorder would overwrite the promotion).
   *
   * NOT CURRENTLY USABLE: verified against test.api.noor.uz on 2026-08-07, the
   * endpoint rejects even a product's own unmodified current ids with
   * `4100 ordered_ids must be exactly this product's current image ids`. The
   * body is parsed correctly (an empty list fails differently, on
   * `ordered_ids`), so this is server-side. `menuApi.updateProduct` therefore
   * asserts the cover through the Image Service instead — an upsert item typed
   * `MAIN` — and relies on write order for position. Swap back here once the
   * backend accepts a valid permutation.
   */
  reorderImages(productId: string, orderedIds: string[]): Promise<void> {
    return putVoid(`merchant/products/${productId}/images/reorder`, {
      ordered_ids: orderedIds,
    });
  },

  /**
   * Stock an existing product in additional branches. Complements
   * `updateRestaurantProducts` (which only patches branches the product is
   * ALREADY in) and `removeFromRestaurants` — together they let an edit move a
   * product between branches.
   *
   * As of 2026-09:
   * - without price: link is created immediately in `list`.
   * - with price on a live branch: returns `pending_change` and empty `list: []`.
   */
  async addRestaurantProducts(
    productId: string,
    patches: RestaurantProductInput[],
  ): Promise<RestaurantProductsResult> {
    const raw = await http.post<unknown>(
      `merchant/products/${productId}/restaurant-products`,
      {
        patches,
      },
    );
    return restaurantProductsResponseSchema.parse(raw);
  },

  /** Patch the branch-local price/stock/status overlays. */
  updateRestaurantProducts(
    productId: string,
    patches: RestaurantProductPatch[],
  ): Promise<ListResult<RestaurantProduct>> {
    return patchList(
      `merchant/products/${productId}/restaurant-products`,
      { patches },
      restaurantProductSchema,
    );
  },

  /**
   * Remove a product from the named branches (the product itself survives).
   *
   * `restaurant_ids` goes in the request BODY, not the query string:
   * `ProductServiceDeleteRestaurantProductsBody` is a **required** requestBody
   * in the spec, and the route rejects a body-less DELETE with `Invalid input`
   * before it ever reads the query. Sending it as a query param is what made
   * an ordinary product edit — any edit that unchecks a branch — fail.
   */
  removeFromRestaurants(productId: string, restaurantIds: string[]): Promise<void> {
    return deleteWithBody(`merchant/products/${productId}/restaurant-products`, {
      restaurant_ids: restaurantIds,
    });
  },
};
